import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { GridRowId } from "@mui/x-data-grid";
import { useCallback, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { DefaultFormAutocomplete } from "../../../../../../common/components/default_form_autocomplete";
import { DefaultMultiAutocomplete } from "../../../../../../common/components/default_multi_autocomplete";
import { DropDownFuncionario } from "../../../../../../common/components/dropdown_funcionario";
import { FormDatePickerInput } from "../../../../../../common/components/masked_inputs/react_forms/form_date_picker_input";
import { FormMoneyMaskedInput } from "../../../../../../common/components/masked_inputs/react_forms/form_money_masked_input";
import { StyledDataGrid } from "../../../../../../common/components/styled_data_grid";
import { CentroCustoDropDown } from "../../../../../../common/domain/models/centro_custo_dropdown";
import { DropDown } from "../../../../../../common/domain/models/dropdown";
import { FuncionarioDropDown } from "../../../../../../common/domain/models/funcionario_dropdown";
import { LogTipoTabela } from "../../../../../../common/utils/log_tabela_tipo";
import { isNotNullOrUndefined } from "../../../../../../common/utils/utils";
import { SaidaProduto } from "../../../../domain/models/saida_produtos_lista";
import {
  SaidaProdutoForm,
  SaidaProdutoSchema,
} from "../../../schemas/Entrada_Produtos/saida_produtos";
import { GenerateEntradaProdutosMultiColumns } from "../columns/saida_produtos_multi";

import DefaultFormInput from "../../../../../../common/components/default_form_input";
import FormContainer from "../../../../../../common/components/form_container";
import PrimaryButton from "../../../../../../common/components/primary_button";

type Props = {
  open: boolean;
  saving: boolean;
  loading: boolean;
  selectedSaidaProduto: SaidaProduto | undefined;
  saidaProduto: SaidaProduto[] | undefined;
  produtosServicosDropDown: DropDown[];
  centroCustoLista: CentroCustoDropDown[];
  selectedProdutosServicosQuery: DropDown[] | null;
  FuncionariosLista: FuncionarioDropDown[];
  handleProdutosServicosQueryChange: (items: DropDown[] | null) => void;
  onClose: () => void;
  onSubmit: (e: SaidaProdutoForm) => void;
  onUpdate: () => void;
  onOpenChangeHistory: (id: number, tabelaTipo: LogTipoTabela) => void;
  OnActionValorChange(valor: string | null, idLinhaGrid: GridRowId): void;
  OnActionCentroCustoChange(
    idCentroCusto: number | null,
    idLinhaGrid: GridRowId,
  ): void;
  OnActionCentroCustoDestinoChange(
    idCentroCusto: number | null,
    idLinhaGrid: GridRowId,
  ): void;
  OnActionQuantidadeChange(
    quantidade: number | null,
    idLinhaGrid: GridRowId,
  ): void;
  OnActionFuncionarioChange(
    idFuncionario: number | null,
    idLinhaGrid: GridRowId,
  ): void;
  OnActionLocalAplicacaoChange(
    localAplicacao: string | null,
    idLinhaGrid: GridRowId,
  ): void;
  OnActionDataChange(data: Date | null, idLinhaGrid: GridRowId): void;
  onNewProdutoServico: () => void;
};

export const SaidaProdutoMultiModal = ({
  open,
  saving,
  loading,
  saidaProduto,
  FuncionariosLista,
  selectedSaidaProduto,
  centroCustoLista,
  produtosServicosDropDown,
  selectedProdutosServicosQuery,
  handleProdutosServicosQueryChange,
  OnActionCentroCustoChange,
  OnActionQuantidadeChange,
  OnActionFuncionarioChange,
  OnActionValorChange,
  OnActionCentroCustoDestinoChange,
  OnActionLocalAplicacaoChange,
  OnActionDataChange,
  onClose,
  onSubmit,
  onOpenChangeHistory,
  onUpdate,
  onNewProdutoServico,
}: Props) => {
  const hookForm = useForm<SaidaProdutoForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(SaidaProdutoSchema),
  });

  useEffect(() => {
    if (saidaProduto && saidaProduto.length === 0) {
      hookForm.reset({ data: new Date() });
    }
  }, [hookForm, saidaProduto]);

  const handleChangeHistory = useCallback(() => {
    if (selectedSaidaProduto) {
      onOpenChangeHistory(
        selectedSaidaProduto.IdSaidaProduto,
        LogTipoTabela.SAIDA_PRODUTO,
      );
    }
  }, [selectedSaidaProduto, onOpenChangeHistory]);

  const columnsDef = useMemo(() => {
    return GenerateEntradaProdutosMultiColumns({
      centroCustoLista: centroCustoLista,
      funcionarioLista: FuncionariosLista,
      OnActionValorChange: OnActionValorChange,
      OnActionCentroCustoChange: OnActionCentroCustoChange,
      OnActionQuantidadeChange: OnActionQuantidadeChange,
      OnActionFuncionarioChange: OnActionFuncionarioChange,
      OnActionCentroCustoDestinoChange: OnActionCentroCustoDestinoChange,
      OnActionDataChange: OnActionDataChange,
      OnActionLocalAplicacaoChange: OnActionLocalAplicacaoChange,
    });
  }, [
    FuncionariosLista,
    OnActionCentroCustoChange,
    OnActionCentroCustoDestinoChange,
    OnActionDataChange,
    OnActionFuncionarioChange,
    OnActionLocalAplicacaoChange,
    OnActionQuantidadeChange,
    OnActionValorChange,
    centroCustoLista,
  ]);

  return (
    <Dialog
      open={open}
      maxWidth={false}
      PaperProps={{
        component: "form",
        onSubmit: hookForm.handleSubmit(onSubmit),
      }}
      sx={{
        "& .MuiDialog-paper": {
          maxWidth: "none",
          width: "98vw",
          margin: "16px",
        },
      }}
    >
      <DialogTitle>Saída de Produtos</DialogTitle>

      <DialogContent>
        <FormContainer title={"Cadastrar/Editar"} arrangement="fullRow">
          <div className="flex flex-col gap-y-4 w-full pt-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormDatePickerInput
                name="data"
                label="Data"
                reactHookForm={hookForm}
                required={true}
              />
              <DefaultMultiAutocomplete
                label="Produto"
                options={produtosServicosDropDown}
                getOptionKey={(x) => x.id}
                getOptionLabel={(x) => x.title}
                value={selectedProdutosServicosQuery}
                required={true}
                onChange={handleProdutosServicosQueryChange}
                onNew={onNewProdutoServico}
              />
            </div>
            <DropDownFuncionario
              name="id_funcionario"
              label="Solicitante"
              reactHookForm={hookForm}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DefaultFormAutocomplete
                name="id_centro_custo"
                label="Centro de Custo Saída"
                newButtonLabel="Novo"
                getOptionLabel={(x) => x.title}
                getOptionKey={(x) => x.id}
                options={centroCustoLista}
                reactHookForm={hookForm}
                onNew={() => { }}
              />
              <DefaultFormAutocomplete
                name="id_centro_custo_destino"
                label="Centro de Custo Destino"
                newButtonLabel="Novo"
                getOptionLabel={(x) => x.title}
                getOptionKey={(x) => x.id}
                options={centroCustoLista}
                reactHookForm={hookForm}
                onNew={() => { }}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DefaultFormInput
                name="quantidade"
                label="Quantidade"
                reactHookForm={hookForm}
              />
              <DefaultFormInput
                name="nr_nota_fiscal"
                label="N° Nota Fiscal"
                reactHookForm={hookForm}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormMoneyMaskedInput
                name="valor_unitario"
                label="Valor Unitário (R$)"
                largeNumbers={true}
                reactHookForm={hookForm}
              />
              <FormMoneyMaskedInput
                name="valor_total"
                label="Valor Total(R$)"
                largeNumbers={true}
                readOnly={true}
                reactHookForm={hookForm}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DefaultFormInput
                name="localAplicacao"
                label="Local de Aplicação"
                reactHookForm={hookForm}
                multiline={true}
                rows={3}
              />
            </div>

            <div className="w-[120px]">
              <PrimaryButton type="submit" buttonColor="primary-green">
                Incluir
              </PrimaryButton>
            </div>
          </div>
        </FormContainer>

        <div style={{ height: 450, width: "100%", marginTop: "24px" }}>
          <StyledDataGrid
            tableName="reajuste_cadastro_lista"
            disableColumnMenu={false}
            showToolbar={true}
            disableRowSelectionOnClick={true}
            getRowId={(row: SaidaProduto) => row.IdSaidaProduto}
            columns={columnsDef}
            checkboxSelection={false}
            rows={saidaProduto ?? []}
            autoHeight={true}
            textSize="medio"
            getRowHeight={() => "auto"}
            rowCount={saidaProduto ? saidaProduto.length : 0}
            pageSizeOptions={[10, 25, 50, 100]}
            paginationMode="server"
          />
        </div>
      </DialogContent>

      <DialogActions>
        <div className="w-full h-full items-center justify-between flex p-4 gap-4">
          <div>
            {isNotNullOrUndefined(selectedSaidaProduto) && (
              <PrimaryButton buttonColor="yellow" onClick={handleChangeHistory}>
                <span className="text-xs">Historico de alterações</span>
              </PrimaryButton>
            )}
          </div>

          <div className="flex flex-row gap-2">
            <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
              <h1
                className="text-sm text-black dark:text-white"
                onClick={onClose}
              >
                Cancelar
              </h1>
            </div>

            <div className="w-[120px]">
              {saving ? (
                <PrimaryButton buttonColor="primary-green">
                  Salvando...
                </PrimaryButton>
              ) : (
                <PrimaryButton
                  onClick={() => onUpdate()}
                  buttonColor="primary-green"
                >
                  Salvar
                </PrimaryButton>
              )}
            </div>
          </div>
        </div>
      </DialogActions>
    </Dialog>
  );
};
